import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
    LogOut, Menu, X, ChevronLeft, Download, FileText, 
    Calendar, User, Loader2, Trash2
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentDetail = () => {
    const { id } = useParams();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [document, setDocument] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        fetchDocument();
    }, [id]);

    const fetchDocument = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/documents/${id}`);
            setDocument(response.data);
        } catch (error) {
            toast.error('Failed to load document');
            navigate('/documents');
        } finally {
            setLoading(false);
        }
    };

    const downloadDocument = async (format) => {
        setDownloading(format);
        try {
            const response = await axios.get(`${API_URL}/api/documents/${id}/export/${format}`, {
                responseType: 'blob'
            });
            
            const contentDisposition = response.headers['content-disposition'];
            let filename = `document.${format === 'word' ? 'docx' : 'pdf'}`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename=(.+)/);
                if (match) filename = match[1];
            }
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = window.document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            window.document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success(`Downloaded as ${format.toUpperCase()}`);
        } catch (error) {
            toast.error(`Failed to download ${format} file`);
        } finally {
            setDownloading(null);
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API_URL}/api/documents/${id}`);
            toast.success('Document deleted');
            navigate('/documents');
        } catch (error) {
            toast.error('Failed to delete document');
        }
        setDeleteDialogOpen(false);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const docTypeLabels = {
        will: 'Last Will & Testament',
        trust: 'Revocable Living Trust',
        poa: 'Power of Attorney',
        living_will: 'Living Will / Advance Directive',
        letter: 'Estate Planning Letter'
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!document) return null;

    return (
        <div className="min-h-screen bg-[#FDFCF8]">
            {/* Header */}
            <header className="sticky top-0 z-50 border-b border-stone-200 bg-white/80 backdrop-blur-sm no-print">
                <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
                    <Link to="/dashboard" className="flex items-center gap-3">
                        <img src="/lion-logo.png" alt="BestWillsAndTrusts" className="h-10 w-10 object-contain" />
                        <span className="font-serif text-xl font-semibold hidden sm:block text-amber-600">BestWillsAndTrusts</span>
                    </Link>
                    
                    <nav className="hidden md:flex items-center gap-8">
                        <Link to="/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground">Dashboard</Link>
                        <Link to="/clients" className="text-sm font-medium text-muted-foreground hover:text-foreground">Clients</Link>
                        <Link to="/documents" className="text-sm font-medium text-primary">Documents</Link>
                        <Link to="/templates" className="text-sm font-medium text-muted-foreground hover:text-foreground">Templates</Link>
                        {user?.role === 'admin' && <Link to="/admin/users" className="text-sm font-medium text-muted-foreground hover:text-foreground">Users</Link>}
                    </nav>

                    <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
                        <Button variant="ghost" size="sm" onClick={logout} className="rounded-none">
                            <LogOut className="h-4 w-4 mr-2" />
                            <span className="hidden sm:inline">Sign Out</span>
                        </Button>
                        <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main content */}
            <main className="max-w-5xl mx-auto px-6 py-8">
                {/* Back button and header */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8 no-print"
                >
                    <Link to="/documents" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Back to Documents
                    </Link>
                    
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="font-serif text-2xl lg:text-3xl mb-2">{document.title}</h1>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                    <FileText className="h-4 w-4" />
                                    {docTypeLabels[document.document_type] || document.document_type}
                                </span>
                                <span className="flex items-center gap-1">
                                    <User className="h-4 w-4" />
                                    {document.client_name}
                                </span>
                                <span className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    {formatDate(document.created_at)}
                                </span>
                            </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                onClick={() => downloadDocument('word')}
                                disabled={downloading === 'word'}
                                className="rounded-none"
                                data-testid="download-word-btn"
                            >
                                {downloading === 'word' ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Download className="h-4 w-4 mr-2" />
                                )}
                                Word
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => downloadDocument('pdf')}
                                disabled={downloading === 'pdf'}
                                className="rounded-none"
                                data-testid="download-pdf-btn"
                            >
                                {downloading === 'pdf' ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Download className="h-4 w-4 mr-2" />
                                )}
                                PDF
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => setDeleteDialogOpen(true)}
                                className="rounded-none text-destructive hover:text-destructive"
                                data-testid="delete-document-btn"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </motion.div>

                {/* Document content */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <Card className="rounded-none border-stone-200 shadow-md">
                        <CardHeader className="no-print">
                            <CardTitle className="font-serif text-lg">Document Preview</CardTitle>
                            <CardDescription>Version {document.version}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="bg-white p-8 min-h-[600px] border border-stone-100">
                                <div className="max-w-none prose prose-sm">
                                    <h2 className="font-serif text-xl text-center mb-6">{document.title}</h2>
                                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                                        {document.content}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Link to client */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mt-6 no-print"
                >
                    <Link to={`/clients/${document.client_id}`}>
                        <Button variant="link" className="p-0">
                            View client details →
                        </Button>
                    </Link>
                </motion.div>
            </main>

            {/* Delete confirmation dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif">Delete Document</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this document? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            className="rounded-none bg-destructive hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default DocumentDetail;
